import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChartWidget } from './dashboardSerializer';
import {
  DashboardDimensionItem,
  DashboardMetricItem,
} from 'domain/payload/response/clientTransmora/projectLayout/ProjectDashboardConfigResp';
import '../templateBuilder/templateBuilder.css';

interface DashboardWidgetCardProps {
  widget: ChartWidget;
  groupIndex: number;
  widgetIndex: number;
  dimensions: DashboardDimensionItem[];
  metrics: DashboardMetricItem[];
  onEditWidget: (widget: ChartWidget, groupId: string) => void;
  onRemoveWidget: (widgetId: string, groupId: string) => void;
  onUpdateSpan: (groupId: string, widgetId: string, newSpan: number) => void;
  onReorderWidgets: (
    sourceGroupId: string,
    targetGroupId: string,
    sourceIndex: number,
    targetIndex: number,
    isBeside?: boolean
  ) => void;
  onInsertWidgetAtIndex: (
    targetGroupId: string,
    targetIndex: number,
    data: any,
    isBeside?: boolean
  ) => void;
  groupId: string;
}

const DashboardWidgetCard: React.FC<DashboardWidgetCardProps> = ({
  widget,
  groupIndex,
  widgetIndex,
  dimensions,
  metrics,
  onEditWidget,
  onRemoveWidget,
  onUpdateSpan,
  onReorderWidgets,
  onInsertWidgetAtIndex,
  groupId,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isResizingX, setIsResizingX] = useState(false);
  const [liveSpan, setLiveSpan] = useState<number | null>(null);
  const [dropSide, setDropSide] = useState<'left' | 'right' | null>(null);

  const secCols = 6;
  const colSpanVal = liveSpan !== null ? liveSpan : Math.min(widget.colSpan || 3, secCols);
  const percentWidth = Math.round((colSpanVal / 6) * 100);

  // Map 6-col grid span to Bootstrap 12-col layout
  const bsColClass =
    colSpanVal >= 6 ? 'col-12' : `col-12 col-md-${Math.min(12, colSpanVal * 2)}`;

  // DRAG REORDER HANDLERS
  const handleWidgetDragStart = (e: React.DragEvent) => {
    e.stopPropagation();
    const payload = {
      type: 'dashboardWidget',
      sourceGroupId: groupId,
      sourceIndex: widgetIndex,
      widget,
    };
    (window as any).__tb_dragged_item = payload;
    if (e.dataTransfer) {
      try {
        const jsonStr = JSON.stringify(payload);
        e.dataTransfer.setData('text/plain', jsonStr);
        e.dataTransfer.setData('application/json', jsonStr);
        e.dataTransfer.effectAllowed = 'copyMove';
      } catch (err) {}
    }
  };

  const handleWidgetDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }

    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      const mouseX = e.clientX;
      const isRight = mouseX > rect.left + rect.width / 2;
      setDropSide(isRight ? 'right' : 'left');
    }
  };

  const handleWidgetDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDropSide(null);
    }
  };

  const handleWidgetDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropSide(null);

    let calculatedSide: 'left' | 'right' = 'right';
    if (cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      const mouseX = e.clientX;
      calculatedSide = mouseX > rect.left + rect.width / 2 ? 'right' : 'left';
    }

    let data = (window as any).__tb_dragged_item;
    if (!data && e.dataTransfer) {
      const dataStr =
        e.dataTransfer.getData('application/json') || e.dataTransfer.getData('text/plain');
      if (dataStr) {
        try {
          data = JSON.parse(dataStr);
        } catch (err) {}
      }
    }

    if (!data) return;

    try {
      const targetIdx = calculatedSide === 'right' ? widgetIndex + 1 : widgetIndex;

      if (data.type === 'dashboardWidget') {
        onReorderWidgets(data.sourceGroupId, groupId, data.sourceIndex, targetIdx, true);
      } else if (
        data.type === 'dashboardChartType' ||
        data.type === 'dashboardDimension' ||
        data.type === 'dashboardMetric'
      ) {
        onInsertWidgetAtIndex(groupId, targetIdx, data, true);
      }
    } catch (err) {
      console.error('Failed to handle widget drop:', err);
    } finally {
      (window as any).__tb_dragged_item = null;
    }
  };

  // HORIZONTAL (RIGHT BORDER) DRAG RESIZE VIA MOUSE DOWN
  const handleRightHandleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!cardRef.current) return;
    const parentContainer = cardRef.current.closest('.row');
    if (!parentContainer) return;

    const containerWidth = parentContainer.getBoundingClientRect().width;
    const singleColWidth = containerWidth / secCols;
    const startX = e.clientX;
    const initialSpan = widget.colSpan || 3;

    setIsResizingX(true);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      const deltaX = moveEvent.clientX - startX;
      const deltaCols = Math.round(deltaX / singleColWidth);
      const calculatedSpan = Math.max(1, Math.min(secCols, initialSpan + deltaCols));
      setLiveSpan(calculatedSpan);
    };

    const handleMouseUp = (upEvent: MouseEvent) => {
      upEvent.preventDefault();
      setIsResizingX(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      const deltaX = upEvent.clientX - startX;
      const deltaCols = Math.round(deltaX / singleColWidth);
      const finalSpan = Math.max(1, Math.min(secCols, initialSpan + deltaCols));
      setLiveSpan(null);
      onUpdateSpan(groupId, widget.id, finalSpan);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const getChartIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'pie':
        return <i className="fas fa-chart-pie" style={{ color: '#8b5cf6' }}></i>;
      case 'heatmap':
        return <i className="fas fa-fire" style={{ color: '#ef4444' }}></i>;
      case 'line':
        return <i className="fas fa-chart-line" style={{ color: '#10b981' }}></i>;
      case 'doughnut':
        return <i className="fas fa-circle-notch" style={{ color: '#6366f1' }}></i>;
      case 'card':
        return <i className="fas fa-id-card" style={{ color: '#0284c7' }}></i>;
      case 'bar':
      default:
        return <i className="fas fa-chart-bar text-primary"></i>;
    }
  };

  // Find dimension names
  const dimNames = (widget.dimensionKey || [])
    .map((dId) => {
      const found = dimensions.find((d) => d.id === dId);
      return found ? found.name : dId;
    })
    .join(', ');

  // Find metric names
  const metricCount = (widget.metricKey || []).length;
  const metricNames = (widget.metricKey || [])
    .map((m) => {
      const found = metrics.find((met) => met.id === m.metricId);
      return found ? found.name : m.metricId;
    })
    .filter(Boolean)
    .join(', ');

  return (
    <div
      className={`${bsColClass} position-relative`}
      onDragOver={handleWidgetDragOver}
      onDragLeave={handleWidgetDragLeave}
      onDrop={handleWidgetDrop}
    >
      {/* ANIMATED INSERTION INDICATOR LINE */}
      {dropSide === 'left' && (
        <motion.div
          initial={{ opacity: 0, scaleY: 0.5 }}
          animate={{ opacity: 1, scaleY: 1 }}
          className="tb-drop-indicator-left"
        />
      )}
      {dropSide === 'right' && (
        <motion.div
          initial={{ opacity: 0, scaleY: 0.5 }}
          animate={{ opacity: 1, scaleY: 1 }}
          className="tb-drop-indicator-right"
        />
      )}

      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        ref={cardRef}
        className={`p-3 tb-resizable-card bg-white rounded-3 border shadow-xs ${
          isResizingX ? 'is-resizing' : ''
        }`}
        style={{ borderLeft: '4px solid #3b82f6' }}
      >
        {/* RESIZE TOOLTIP BADGE */}
        {isResizingX && <div className="tb-resize-tooltip">{`Width: ${percentWidth}%`}</div>}

        {/* CARD TOOLBAR WITH DRAG GRIP */}
        <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
          <div
            className="d-flex align-items-center gap-2 overflow-hidden me-2 cursor-grab select-none"
            draggable={true}
            onDragStart={handleWidgetDragStart}
            title="Click and drag here to reorder position"
            style={{ userSelect: 'none' }}
          >
            <i className="fas fa-grip-vertical text-secondary ms-1 me-1"></i>
            <span className="fs-6 me-1">{getChartIcon(widget.chartType)}</span>
            <strong className="small text-truncate text-dark" style={{ fontSize: '13px' }}>
              {widget.chartName}
            </strong>
          </div>

          {/* ACTION BUTTONS */}
          <div className="d-flex align-items-center gap-1 flex-shrink-0">
            <button
              type="button"
              className="tb-btn tb-btn-icon"
              title="Edit Widget Settings"
              onClick={() => onEditWidget(widget, groupId)}
            >
              <i className="fas fa-cog text-secondary" style={{ fontSize: '11px' }}></i>
            </button>
            <button
              type="button"
              className="tb-btn tb-btn-icon tb-btn-danger"
              title="Delete Widget"
              onClick={() => onRemoveWidget(widget.id, groupId)}
            >
              <i className="fas fa-trash-alt" style={{ fontSize: '11px' }}></i>
            </button>
          </div>
        </div>

        {/* WIDGET PREVIEW BODY */}
        <div className="py-2">
          {widget.chartType === 'heatmap' ? (
            <div className="rounded-3 bg-light p-3 border text-center">
              <i className="fas fa-fire fa-2x mb-2 opacity-75" style={{ color: '#ef4444' }}></i>
              <div className="small fw-semibold text-dark">{widget.chartName}</div>
              <div className="text-muted text-truncate px-2" style={{ fontSize: '11px' }}>
                {metricCount > 0
                  ? `${metricCount} Metrics (${metricNames})`
                  : 'No Metrics Assigned'}
              </div>
            </div>
          ) : widget.chartType === 'pie' || widget.chartType === 'doughnut' ? (
            <div className="rounded-3 bg-light p-3 border text-center">
              <i className="fas fa-chart-pie fa-2x text-purple-500 mb-2 opacity-75" style={{ color: '#8b5cf6' }}></i>
              <div className="small fw-semibold text-dark">{widget.chartName}</div>
              <div className="text-muted" style={{ fontSize: '11px' }}>
                Global Distribution Chart
              </div>
            </div>
          ) : (
            <div className="rounded-3 bg-light p-3 border text-center">
              <i className="fas fa-chart-bar fa-2x text-primary mb-2 opacity-75"></i>
              <div className="small fw-semibold text-dark">{widget.chartName}</div>
              <div className="text-muted text-truncate px-2" style={{ fontSize: '11px' }}>
                {dimNames ? `Dimension: ${dimNames}` : 'No Dimension Assigned'}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT BORDER RESIZE HANDLE */}
        <div
          className={`tb-resize-handle-right ${isResizingX ? 'is-active' : ''}`}
          onMouseDown={handleRightHandleMouseDown}
          title="Drag border left/right to resize widget column width"
        />
      </motion.div>
    </div>
  );
};

export default DashboardWidgetCard;
